/**
 * Vocabulary of the whole-entry mapping-table CSV: one row = one entry.
 *
 * Distinct from `RULE_CSV_FORMAT` (`Common.ts`), which describes the
 * value-mappings *rule* CSV — that file carries the rules inside a single
 * entry, this one carries the entries themselves. Both the admin-console
 * server (parse, diff, serialize) and the browser (column reference, preview
 * labels) read this, so the two halves cannot drift.
 */

import { CLINICAL_CODE_KINDS } from './ClinicalCodes.ts';
import { OBSERVATION_CODE_SYSTEMS } from './Ecart.ts';

export const ENTRY_CSV_FORMAT = {
  /**
   * Output cell that nulls an optional field. A blank cell means "leave as
   * is" so a narrow file (lookup_key + display) can't strip fields it has no
   * opinion about; clearing has to be explicit.
   */
  CLEAR: 'CLEAR',
  /** Separator inside a list cell (facilities, unit overrides). */
  LIST: '|',
  /** Separator between repeats of a structured cell (components, mirrored fields). */
  REPEAT: '~',
  /** Separator between the parts of one structured repeat. */
  PART: '^',
  /** Separator inside a `raw=ucum` unit-override pair. */
  PAIR: '='
} as const;

/**
 * Short aliases accepted in the `system` column, so a 2,000-row file doesn't
 * repeat `urn:agilemd:fhir:observation:code-system:internal`. The full URI is
 * accepted too; a blank cell defaults to LOINC, which is what the vast
 * majority of bulk observation rows are.
 */
export const OBSERVATION_SYSTEM_ALIASES: Readonly<Record<string, string>> = Object.freeze({
  LOINC: OBSERVATION_CODE_SYSTEMS.LOINC,
  SNOMED: OBSERVATION_CODE_SYSTEMS.SNOMED,
  'SNOMED-CT': OBSERVATION_CODE_SYSTEMS.SNOMED,
  SNOMEDCT: OBSERVATION_CODE_SYSTEMS.SNOMED,
  AGILEMD: OBSERVATION_CODE_SYSTEMS.AGILEMD_INTERNAL,
  INTERNAL: OBSERVATION_CODE_SYSTEMS.AGILEMD_INTERNAL
});

export const ENTRY_CSV_DEFAULT_SYSTEM = OBSERVATION_CODE_SYSTEMS.LOINC;

/**
 * Canonical column names. Headers are matched case-insensitively with `-`,
 * `_` and spaces treated alike, so `Lookup Key`, `lookup-key` and
 * `lookup_key` all land on the same column.
 */
export const CLINICAL_CODE_CSV_COLUMNS = {
  LOOKUP_KEY: 'lookup_key',
  VENDOR: 'vendor',
  INSTANCE: 'instance',
  FACILITIES: 'facilities',
  NOTES: 'notes',
  KIND: 'kind',
  CODE: 'code',
  SYSTEM: 'system',
  DISPLAY: 'display',
  CATEGORY: 'category',
  UCUM: 'ucum',
  UNIT_OVERRIDES: 'unit_overrides',
  COMPONENTS: 'components',
  QUESTIONNAIRE: 'questionnaire',
  LINK_ID: 'link_id',
  TEXT: 'text',
  VALUE_MAPPING_KEY: 'value_mapping_key',
  MIRRORED_FIELDS: 'mirrored_fields',
  /**
   * Written by the export so you can see which rows carry an `expansion` the
   * CSV can't represent. Read-only: the import accepts and ignores it rather
   * than rejecting it as unknown, so an exported file re-imports cleanly.
   */
  HAS_EXPANSION: 'has_expansion'
} as const;

export type ClinicalCodeCsvColumn =
  (typeof CLINICAL_CODE_CSV_COLUMNS)[keyof typeof CLINICAL_CODE_CSV_COLUMNS];

/** Columns the import reads but never writes. */
export const ENTRY_CSV_READ_ONLY_COLUMNS: readonly string[] = Object.freeze([
  CLINICAL_CODE_CSV_COLUMNS.HAS_EXPANSION
]);

/** The row's identity: what create-vs-update is decided on. */
export const ENTRY_CSV_IDENTITY_COLUMNS: readonly string[] = Object.freeze([
  CLINICAL_CODE_CSV_COLUMNS.LOOKUP_KEY,
  CLINICAL_CODE_CSV_COLUMNS.VENDOR,
  CLINICAL_CODE_CSV_COLUMNS.INSTANCE,
  CLINICAL_CODE_CSV_COLUMNS.FACILITIES
]);

/** Columns whose meaning depends on the row's `kind`. */
export const CLINICAL_CODE_CSV_KIND_COLUMNS: Readonly<Record<string, readonly string[]>> =
  Object.freeze({
    [CLINICAL_CODE_KINDS.OBSERVATION]: Object.freeze([
      CLINICAL_CODE_CSV_COLUMNS.CODE,
      CLINICAL_CODE_CSV_COLUMNS.SYSTEM,
      CLINICAL_CODE_CSV_COLUMNS.DISPLAY,
      CLINICAL_CODE_CSV_COLUMNS.CATEGORY,
      CLINICAL_CODE_CSV_COLUMNS.UCUM,
      CLINICAL_CODE_CSV_COLUMNS.UNIT_OVERRIDES,
      CLINICAL_CODE_CSV_COLUMNS.COMPONENTS
    ]),
    [CLINICAL_CODE_KINDS.QUESTIONNAIRE_RESPONSE]: Object.freeze([
      CLINICAL_CODE_CSV_COLUMNS.QUESTIONNAIRE,
      CLINICAL_CODE_CSV_COLUMNS.LINK_ID,
      CLINICAL_CODE_CSV_COLUMNS.TEXT,
      CLINICAL_CODE_CSV_COLUMNS.VALUE_MAPPING_KEY,
      CLINICAL_CODE_CSV_COLUMNS.MIRRORED_FIELDS
    ]),
    [CLINICAL_CODE_KINDS.IGNORE]: Object.freeze([])
  });

/**
 * What one CSV row does to the table. There is no `remove`: a row absent from
 * the file is left alone. At whole-entry scope a mis-scoped file would
 * otherwise propose deleting everything it omits.
 */
export const ENTRY_IMPORT_OP_KINDS = {
  CREATE: 'create',
  UPDATE: 'update',
  UNCHANGED: 'unchanged'
} as const;

export type EntryImportOpKind = (typeof ENTRY_IMPORT_OP_KINDS)[keyof typeof ENTRY_IMPORT_OP_KINDS];

/**
 * Rows per bulk request. The console chunks a large file so one import does
 * not hold a single long transaction against the edit version; each chunk
 * commits or rolls back on its own.
 */
export const ENTRY_BULK_CHUNK_SIZE = 500;

/** Hard ceiling per bulk request, enforced by the API. */
export const ENTRY_BULK_MAX_ROWS = 1000;

/**
 * Changes returned in a preview. A 2,000-row file is reviewed by its counts
 * and its errors, not by scrolling every row, so the sample is capped and the
 * client says how many more there are.
 */
export const ENTRY_IMPORT_PREVIEW_SAMPLE = 200;
